/**
 * Client for the trading service's cost routes: the charge library (C1), the
 * pricing engine (C2), the capacity review (C7) and PPA settlement (C5).
 *
 * Everything here goes through fetchWithAuth, so every call carries the
 * verified token and the backend scopes the response to the caller's
 * organisation. Nothing in this module invents a number: a component the
 * backend could not price arrives with `gbp: null` and a reason, and the
 * rendering helpers in energy-quantity.ts show the reason, never a zero.
 */
import { fetchWithAuth } from "@/lib/api";

export type RateRow = {
  id: string;
  organisation_id: string | null;
  component: string;
  dno_region_id: number | null;
  tnuos_zone: number | null;
  tariff_code: string | null;
  band: string | null;
  voltage_level: string | null;
  unit: string;
  value: number | null;
  charge_year: string;
  effective_from: string;
  effective_to: string | null;
  publication: string;
  publication_url: string | null;
  publication_date: string | null;
  status: "confirmed" | "derived" | "to_be_confirmed" | "contracted" | "synthetic";
  note: string | null;
  version: string;
};

export type TimeBandRow = {
  id: string;
  organisation_id: string | null;
  dno_region_id: number;
  tariff_code: string;
  band: string;
  day_kind: string;
  start_local: string | null;
  end_local: string | null;
  charge_year: string;
  effective_from: string;
  publication: string;
  status: string;
  version: string;
};

export type LibraryResponse = {
  library_version: string;
  organisation_id: string;
  regions: Array<{ region_id: number; name: string; licensee: string; group: string; duos_publication: string; tnuos_zone: number }>;
  tnuos_zones: Array<{ zone: number; name: string }>;
  duos_tariffs: Array<{ code: string; label: string }>;
  components: Record<string, { label: string; basis: string; unit: string; scope: string }>;
  capacity_bands: { publication: string; bands: Array<{ voltage_level: string; band: string; min_kva: number; max_kva: number | null }> };
  summary: { published: Record<string, number>; own: Record<string, number>; published_rows: number; own_rows: number };
  rates: RateRow[];
  time_bands: TimeBandRow[];
  maintenance: { rule: string; republish_endpoint: string; precedence: string };
};

export type MpanConfig = {
  id: string;
  organisation_id: string;
  workspace_id: string | null;
  mpan: string;
  dno_region_id: number;
  voltage_level: string;
  duos_tariff_code: string;
  tnuos_zone: number | null;
  assigned_tcr_band: string | null;
  agreed_supply_capacity_kva: number | null;
  asc_source: string | null;
  power_factor: number | null;
  ccl_exempt: boolean;
  effective_from: string;
  effective_to: string | null;
  source: string;
  note: string | null;
};

export type ComponentLine = {
  component: string;
  label: string;
  basis: string;
  unit: string;
  rate: number | null;
  gbp: number | null;
  status: string;
  source: string;
  version: string | null;
  band: string | null;
  reason: string | null;
  effective_from: string | null;
  row_id: string | null;
};

export type PricedQuantity = {
  mpan: string;
  direction: "import" | "export";
  period_start: string;
  kwh: number;
  gbp: number | null;
  currency: string;
  tariff_version: string;
  priced: boolean;
  partially_priced: boolean;
  duos_band: string | null;
  lines: ComponentLine[];
  unpriced: Array<{ component: string; label: string; reason: string }>;
  reason: string | null;
};

export type PriceResponse = {
  organisation_id: string;
  mpan: string;
  results: PricedQuantity[];
  totals: { kwh: number; gbp: number | null; priced: boolean; fully_priced_items: number; items: number };
};

export type StackResponse = {
  mpan: string;
  at: string;
  per_kwh_import: PricedQuantity;
  per_kwh_export: PricedQuantity;
  per_day: {
    config: MpanConfig | null;
    implied_tcr_band: string | null;
    assigned_tcr_band: string | null;
    band_used: string | null;
    gbp_per_day: number | null;
    priced: boolean;
    lines: ComponentLine[];
    unpriced: Array<{ component: string; label: string; reason: string }>;
    tariff_version: string;
    reason?: string;
  };
};

export class CostApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = (await fetchWithAuth(path, init)) as Response;
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = await res.json();
      detail = typeof body?.detail === "string" ? body.detail : JSON.stringify(body);
    } catch {
      /* body was not JSON */
    }
    throw new CostApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export function getLibrary(params?: { component?: string; dno_region_id?: number }): Promise<LibraryResponse> {
  const q = new URLSearchParams();
  if (params?.component) q.set("component", params.component);
  if (params?.dno_region_id) q.set("dno_region_id", String(params.dno_region_id));
  const qs = q.toString();
  return call<LibraryResponse>(`/api/trading/cost/library${qs ? `?${qs}` : ""}`);
}

export function getMpans(): Promise<{ organisation_id: string; mpans: MpanConfig[] }> {
  return call(`/api/trading/cost/mpans`);
}

export function getStack(mpan: string, at?: string): Promise<StackResponse> {
  const q = new URLSearchParams({ mpan });
  if (at) q.set("at", at);
  return call<StackResponse>(`/api/trading/cost/stack?${q.toString()}`);
}

export type PriceItem = { period_start: string; kwh: number; direction?: "import" | "export" };

export function priceItems(mpan: string, items: PriceItem[]): Promise<PriceResponse> {
  return call<PriceResponse>(`/api/trading/cost/price`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mpan, items }),
  });
}

export function republishRate(row: Partial<RateRow> & { scope?: "own" | "published" }): Promise<RateRow> {
  return call<RateRow>(`/api/trading/cost/library/rates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(row),
  });
}

export const STATUS_LABEL: Record<string, string> = {
  confirmed: "confirmed against publication",
  derived: "derived from confirmed rows",
  to_be_confirmed: "to be confirmed",
  contracted: "contracted rate",
  synthetic: "synthetic (demo realm)",
  not_applicable: "not applicable",
};
